import _ from 'lodash';
import { DeepPartial } from 'redux';
import { v4 as uuidV4 } from 'uuid';
import { FormQuestionTypeKeys } from 'ui-model';

import { omitExtendedFields } from '../resources/createResource';
import {
  Appointment,
  Language,
  Patient,
  PatientForm,
  PatientFormAnswer,
  PatientFormQuestion,
  PatientFormStatus,
  PatientFormTemplate,
  PatientFormTemplateItem,
  PatientFormTemplateItemAnswer,
} from '../resources/types';
import normalizeJumpLogicV2Response, { IJumpLogicV2Response } from '../utils/normalizeJumpLogicV2Response';

import { ITEMS } from './patientFormTemplatesService';
import { updateText } from './stringsService';

export const defaultAnswerOther = {
  id: 'other',
  label: 'Other',
  selected: false,
  text: 'Other',
};

export const defaultAnswerNoneOfAbove = {
  id: 'none',
  label: 'None of above',
  selected: false,
  text: 'None of above',
};

export function createQuestion(item: PatientFormTemplateItem): PatientFormQuestion {
  if (item.type === 'group' || item.type === 'patient_information' || item.type === 'demographics') {
    return {
      ...item,
      text: item.text || item.title || '',
      answers: [], // not used for 'group'
      properties: {
        ...item.properties,
        items: _.map(item.properties.items, createQuestion),
      },
    };
  }

  //
  // CCDA question types
  //
  // We are currently assuming that the answer of a question would be a 'string' or an 'array of strings'.
  // However, there are cases when we need to have an array of objects for answers instead
  // and we would need to break the pattern.
  //
  if (item.type === 'medication_list') {
    return {
      ...item,
      text: item.text || item.title || '',
      answers: [],
      noMedication: false,
    } as PatientFormQuestion;
  }

  if (item.type === 'allergy') {
    return {
      ...item,
      text: item.text || item.title || '',
      answers: [],
      noAllergy: false,
    } as PatientFormQuestion;
  }

  if (item.type === 'immunization_history' || item.type === 'surgical_history' || item.type === 'family_history') {
    return {
      ...item,
      text: item.text || item.title || '',
      answers: [],
    } as PatientFormQuestion;
  }

  const { canSelectAnswer } = ITEMS[item.type];
  const answers = canSelectAnswer
    ? _.map(item.properties.answers, (answer) => ({
        ...answer,
        id: answer.id,
        text: answer.text || answer.label || '',
        selected: false,
      }))
    : [
        {
          id: '',
          text: '',
          selected: false,
        },
      ];

  return {
    ...item,
    text: item.text || item.title || '',
    answers,
  } as PatientFormQuestion;
}

export function createPatientForm(
  patientFormTemplate: PatientFormTemplate,
  patient?: string,
  appointment?: string,
  forceCurrentItemId?: string,
): DeepPartial<PatientForm> {
  const {
    id,
    template: { items },
  } = patientFormTemplate;
  const questionsForStaffReview = _.get(patientFormTemplate, 'questionsForStaffReview', {});
  const categoryNameToWritebackPDF = _.get(patientFormTemplate, 'categoryNameToWritebackPDF');
  const writebackPDFToEhr = _.get(patientFormTemplate, 'writebackPDFToEhr', false);
  const writebackToEhr = _.get(patientFormTemplate, 'writebackToEhr', false);
  const writebackOptions = _.get(patientFormTemplate, 'writebackOptions', {});
  const jumpLogic = _.get(patientFormTemplate, 'jumpLogic', {});

  const currentItemId = forceCurrentItemId || _.get(_.head(items), 'id', '');
  const path = [currentItemId];
  const pages = [{ questions: _.map(items, (item) => createQuestion(item)) }];

  return {
    ...(categoryNameToWritebackPDF ? { categoryNameToWritebackPDF } : {}),
    writebackPDFToEhr,
    jumpLogic,
    writebackToEhr,
    patient: { id: patient } as Patient,
    patientFormTemplate: { id } as PatientFormTemplate,
    appointment: { id: appointment } as Appointment,
    status: PatientFormStatus.Started,
    responses: { currentItemId, path, pages },
    questionsForStaffReview,
    writebackOptions,
  };
}

export function getTranslation(
  item: PatientFormQuestion | PatientFormTemplateItem | PatientFormTemplateItemAnswer,
  language: Language,
  fieldName:
    | 'title'
    | 'description'
    | 'label'
    | 'schedulerButton'
    | 'properties.template'
    | 'properties.actionLabel'
    | 'properties.actionHref',
) {
  // NOTE: We can't rename `locales` to `languages` b/c we need to support
  // forms that are currently stored on the DB
  return (
    _.get(item, `locales.${language}.${fieldName}`) ||
    _.get(item, `properties.locales.${language}.${fieldName}`) ||
    _.get(item, fieldName) ||
    _.get(item, `properties.${fieldName}`)
  );
}

function getItemChoice(item: PatientFormQuestion | PatientFormTemplateItem, language: Language): string {
  const answers = _.get(item, 'answers', []);

  if (ITEMS[item.type].canSelectAnswer) {
    return _(answers)
      .filter('selected')
      .map((answer) => getTranslation(answer, language, 'label'))
      .value()
      .join(',');
  }

  // fixing parse error for docusign answer
  return _.get(answers, 'event') ? answers : _.get(_.head(answers), 'text', '');
}

function getSquigglyReplacement(item: PatientFormQuestion | PatientFormTemplateItem, language: Language) {
  if (item.properties.items && item.properties.items.length) {
    // eslint-disable-next-line no-use-before-define
    return getSquigglyReplacements(item.properties.items, language);
  }
  const squiggly = `{{${item.squiggly}}}`;
  return {
    prev: squiggly,
    next: getItemChoice(item, language) || squiggly,
  };
}

export function getSquigglyReplacements(items: any[], language: Language): any[] {
  return _.flatMap(items, (item) => getSquigglyReplacement(item, language));
}

// TODO: split functionality for PatientFormQuestion | PatientFormTemplateItem
export function parseItem(item: any, language: Language, replacements: any[] | undefined = []): any {
  return {
    ...item,
    title: updateText(getTranslation(item, language, 'title'), replacements),
    description: updateText(getTranslation(item, language, 'description'), replacements),
    // TODO: Unify data structure for `PatientForm.answers` and `PatientFormTemplate.properties.answers`
    // fixing parse error for docusign answer
    answers: _.get(item, 'answers.event')
      ? _.get(item, 'answers')
      : _.map(_.get(item, 'answers'), (answer) => ({
          ...answer,
          label: getTranslation(answer, language, 'label'),
        })),
    properties: {
      ...item.properties,
      // fixing parse error for docusign answer
      answers: _.get(item, 'answers.event')
        ? _.get(item, 'answers')
        : _.map(_.get(item.properties, 'answers'), (answer) => ({
            ...answer,
            label: getTranslation(answer, language, 'label'),
          })),
      items: item.properties.items
        ? _.map(item.properties.items, (nestedItem) => parseItem(nestedItem, language, replacements))
        : undefined,
      template: getTranslation(item, language, 'properties.template'),
      actionLabel: getTranslation(item, language, 'properties.actionLabel'),
      actionHref: getTranslation(item, language, 'properties.actionHref'),
    },
  };
}

export function parseItems(items: any[], language: Language, replacements: any[] | undefined = []) {
  return _.map(items, (item) => parseItem(item, language, replacements));
}

export function getItemsTranslated(patientForm: PatientForm, language: Language) {
  const items = _.get(patientForm, 'responses.pages[0].questions');
  const squigglyReplacements = getSquigglyReplacements(items, language);
  return parseItems(items, language, squigglyReplacements);
}

export function setAnswerToQuestion(question: PatientFormQuestion, value: any): PatientFormQuestion {
  if (question.type === 'group' || question.type === 'patient_information' || question.type === 'demographics') {
    return {
      ...question,
      answers: question.type === 'demographics' ? value : question.answers, // fallback for demographics question answer
      properties: {
        ...question.properties,
        items: _.map(question.properties.items, (item) =>
          setAnswerToQuestion(item as PatientFormQuestion, value[item.id]),
        ),
      },
    };
  }

  //
  // CCDA question types
  //
  // We are currently assuming that the answer of a question would be a 'string' or an 'array of strings'.
  // However, there are cases when we need to have an array of objects for answers instead
  // and we would need to break the pattern.
  //
  if (question.type === 'medication_list') {
    return {
      ...question,
      answers: value ? value.medicationDataObjects : [],
    };
  }

  if (question.type === 'scheduler') {
    return {
      ...question,
      answers: [
        {
          // @ts-ignore
          bookingData: value,
        },
      ],
    };
  }

  if (
    question.type === 'allergy' ||
    question.type === 'immunization_history' ||
    question.type === 'surgical_history' ||
    question.type === 'family_history' ||
    question.type === 'social_history'
  ) {
    return {
      ...question,
      answers: _.map(value, (it) => ({ ...it, id: it.id || uuidV4() })) || [],
    };
  }

  if (question.type === 'docusign_templates') {
    return {
      ...question,
      answers: value || [],
    };
  }

  if (question.type === 'insurance_verification') {
    return {
      ...question,
      insurances: value || [],
    };
  }

  if (question.type === 'insurance_capture') {
    const selectedAnswerId = _.get(value, 'answerId', '');
    const answers = question.answers.map((it) => (it.id === selectedAnswerId ? { ...it, selected: true } : it));
    return {
      ...question,
      insurances: _.get(value, 'insurances', []),
      eligibilityResults: _.get(value, 'eligibilityResults', []),
      answers,
    };
  }

  if (question.type === 'insurance_card') {
    const buildInsuranceAnswer = (answer: PatientFormAnswer | undefined, answerValue: unknown) => ({
      ...answer,
      text: answer?.text || omitExtendedFields(answerValue),
      selected: true,
      value: _.get(answer, 'value') || answer?.text || '',
      id: answer?.id || uuidV4(),
    });
    const valueAsArray = Array.isArray(value) ? value : [];
    const currentAnswers = _.map(question.answers, (answer, idx) => buildInsuranceAnswer(answer, valueAsArray[idx]));
    const currentInsurancesOnAnswers: Record<string, boolean> = {};
    currentAnswers.forEach((it) => {
      if (_.get(it, 'text._id')) {
        currentInsurancesOnAnswers[_.get(it, 'text._id')] = true;
      }
    });
    const newValues = valueAsArray.filter((it) => !currentInsurancesOnAnswers[_.get(it, '_id')]);
    const newAnswers = newValues.map((it) => buildInsuranceAnswer(undefined, it));
    const normalizedQuestion = {
      ...question,
      answers: [...currentAnswers, ...newAnswers],
    };

    return normalizedQuestion;
  }

  const { canSelectAnswer } = ITEMS[question.type];
  const { answers } = question;
  const answerOtherOnValue = _.find(value, { id: defaultAnswerOther.id });
  if (answerOtherOnValue) {
    _.remove(answers, { id: defaultAnswerOther.id });
    answers.push(answerOtherOnValue);
  } else if (_.find(value, { id: defaultAnswerNoneOfAbove.id })) {
    _.remove(answers, { id: defaultAnswerNoneOfAbove.id });
    answers.push(defaultAnswerNoneOfAbove);
  }
  return {
    ...question,
    answers: _.map(answers, ({ selected, ...answer }) => ({
      ...answer,
      // save text when it's an open text, save selection otherwise
      text: canSelectAnswer ? answer.text : omitExtendedFields(value),
      selected: canSelectAnswer ? Boolean(_.find(value, { id: answer.id })) || _.includes(value, answer.id) : true,
      value: _.get(answer, 'value', _.get(answer, 'text', '')),
      id: answer.id || uuidV4(),
    })),
  };
}

function formatValue(question: PatientFormQuestion, value: any) {
  // BE expect an array of strings, otherwise PDF download will be buggy
  if (question.type === 'medication_search' && _.isString(value)) return _.split(value, ', ');
  return value;
}

export function setAnswer(
  questions: PatientFormQuestion[],
  itemId: string,
  value: any,
  nextItemId?: string,
  includeNested?: boolean,
): PatientFormQuestion[] {
  const currentQuestion = _.find(questions, { id: itemId });
  const nextQuestion = _.find(questions, { id: nextItemId });

  return _.map(questions, (question) => {
    if (question.id !== itemId) {
      // store the id of the previous question
      if (_.get(nextQuestion, 'id', '') === question.id) {
        // do not overwrite previousItemId on next question if previousItemId on current question is equal nextItemId
        // to prevent it to create a circular referency that results on a infinite loop since the function to calculate question number is recursive
        if (!_.get(nextQuestion, 'properties.previousItemId')) {
          const previousItemId =
            _.get(currentQuestion, 'type') === FormQuestionTypeKeys.jump
              ? _.get(currentQuestion, 'properties.previousItemId', itemId)
              : itemId;

          return {
            ...question,
            properties: { ...question.properties, previousItemId },
          };
        }
        return question;
      }
      // Set answer inside nested questions if `includeNested`.
      // Useful when we don't want to submit grouped questions all at once
      if (includeNested && question.properties.items) {
        return {
          ...question,
          properties: {
            ...question.properties,
            items: setAnswer(question.properties.items, itemId, formatValue(question, value), nextItemId),
          },
        };
      }

      return question;
    }

    return setAnswerToQuestion(question, formatValue(question, value));
  });
}

export function getQuestionsFlatten(patientForm: PatientForm) {
  const pages = _.get(patientForm.responses, 'pages', []);
  return _.flatMap(pages, 'questions') as PatientFormQuestion[];
}

export function getRedirectConfig(question: PatientFormQuestion, jumpLogicResponse?: IJumpLogicV2Response): any {
  const config = _.pick(question, ['type', 'squiggly', 'redirectType']);
  const normalizedJumpLogicResponse = jumpLogicResponse ? normalizeJumpLogicV2Response(jumpLogicResponse) : null;

  const propertyPath = [];
  switch (config.redirectType) {
    case 'goTo':
      propertyPath.push('goTo');
      break;
    case 'goToForm':
      propertyPath.push('goToForm');
      break;
    case 'scheduler':
    case 'reschedule':
      propertyPath.push('params');
      break;
    case 'externalUrl':
      propertyPath.push('url');
      break;
    default:
      break;
  }

  const jumpV2Config = {
    ...(normalizedJumpLogicResponse?.type ? { redirectType: normalizedJumpLogicResponse.type } : {}),
    properties: {
      ...(normalizedJumpLogicResponse?.value
        ? {
            goTo: normalizedJumpLogicResponse.value,
            url: normalizedJumpLogicResponse.value,
            goToForm: normalizedJumpLogicResponse?.type === 'goToForm' ? normalizedJumpLogicResponse.value : undefined,
          }
        : {}),
      ...(normalizedJumpLogicResponse?.params
        ? {
            params: {
              ..._.pick(normalizedJumpLogicResponse?.params, ['facility', 'provider', 'appointmentType']),
            },
            ..._.omit(normalizedJumpLogicResponse?.params, ['facility', 'provider', 'appointmentType']),
          }
        : {}),
    },
  };

  return {
    ...config,
    ...jumpV2Config,
    properties: {
      ...question.properties,
      ..._.pick(question.properties, propertyPath),
      hideCalendar: _.get(question, 'properties.hideCalendar', false),
      showFilters: _.get(question, 'properties.showFilters', false),
      showZipcodeFilter: _.get(question, 'properties.showZipcodeFilter', false),
      hideSendCode: _.get(question, 'properties.hideSendCode', false),
      // override params from jumpV2Config
      ...jumpV2Config.properties,
    },
  };
}

export function getQuestionScore(question: PatientFormQuestion) {
  const scoringEnabled = _.get(question, 'properties.scoringEnabled');
  return scoringEnabled
    ? _.sumBy(_.filter(question.answers, 'selected'), (answer) => parseInt(answer.score || '0', 10))
    : 0;
}
